
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Preview } from "./Preview";
import { defaultFunnelData } from "./constants";
import { getAllFunnelTemplates } from "@/lib/templates";
import type { FunnelTemplate } from "./SectionTemplates/templates";
import type { FunnelData } from "./types";
import { Sparkles } from "lucide-react";

interface FunnelTemplatesProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (data: FunnelData) => void;
  isPro?: boolean;
}

export const FunnelTemplates = ({ isOpen, onClose, onSelect, isPro = false }: FunnelTemplatesProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<FunnelData | null>(null);
  const [templates, setTemplates] = useState<FunnelTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const loadedTemplates = await getAllFunnelTemplates();
        setTemplates(loadedTemplates);
      } catch (error) {
        console.error("Error loading templates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const handleSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      if (template.isPro && !isPro) {
        return; // Pro template but not a pro funnel
      }
      setSelectedTemplate(templateId);
      setPreviewData(template.data);
    }
  };

  const handleCreate = () => {
    if (selectedTemplate) {
      const template = templates.find(t => t.id === selectedTemplate);
      if (template) {
        onSelect(template.data);
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Choose a Template</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 max-h-[600px] overflow-y-auto pr-4">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleSelect(template.id)}
                  className={`relative overflow-hidden rounded-lg p-6 text-left transition-all duration-200 hover:scale-105 ${
                    selectedTemplate === template.id
                      ? "ring-2 ring-primary ring-offset-2"
                      : "bg-white/50 hover:bg-white/80"
                  } ${template.isPro && !isPro ? "opacity-50" : ""}`}
                  disabled={template.isPro && !isPro}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg">{template.title}</h3>
                      <div className="flex gap-2">
                        <Badge variant="secondary" className="text-xs">
                          Template
                        </Badge>
                        {template.isPro && (
                          <Badge variant="default" className="text-xs bg-gradient-to-r from-purple-500 to-purple-900">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Pro
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {template.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!selectedTemplate}
              >
                Create Funnel
              </Button>
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden bg-slate-50">
            {previewData ? (
              <Preview data={previewData} />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Select a template to preview
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
